import { Sprite } from 'pixi.js';

import { Shared } from '../../App';
import { CullingComponent } from '../components/CullingComponent';
import { Game } from '../Game';
import { PhysicsCollisionGroup, PhysicsShapeType, PhysicsUtils } from '../physics';
import { PhysicsComponent } from '../physics/PhysicsComponent';
import { BaseEntity } from './BaseEntity';

export const defaultSeeSawParams = {
    label: 'SeeSaw',
    r: 0,
    x: 0,
    y: 0,
    sx: 1,
    sy: 1,
    props: {
        angle: 0.5,
    },
};

export type SeeSawParams = typeof defaultSeeSawParams;

export class SeeSawEntity extends BaseEntity
{
    public stand: Sprite;
    public bar: Sprite;
    public params!: SeeSawParams;
    public physics: PhysicsComponent;
    public culling: CullingComponent;

    constructor()
    {
        super();
        this.physics = this.addComponent(PhysicsComponent);
        this.culling = this.addComponent(CullingComponent);
        this.physics.harmPlayer = true;

        this.stand = Sprite.from(Shared.assets.getTexture('seesaw-stand'));
        this.stand.anchor.set(0.5);
        this.view.addChild(this.stand);

        this.bar = Sprite.from(Shared.assets.getTexture('seesaw-bar'));
        this.bar.anchor.set(0.5);
        this.view.addChild(this.bar);
    }

    public build(params: Partial<SeeSawParams> = {}): void
    {
        this.physics.clearParts();
        this.params = { ...defaultSeeSawParams, ...params };

        const absx = Math.abs(this.params.sx);
        const absy = Math.abs(this.params.sy);

        const body = PhysicsUtils.createBody({
            mass: 1,
            x: this.params.x,
            y: this.params.y - (45 * this.params.sy),
            fixedX: true,
            fixedY: true,
        }, {
            type: PhysicsShapeType.BOX,
            width: 340 * absx,
            height: 20 * absy,
            group: PhysicsCollisionGroup.SPECIAL,
            mask: PhysicsCollisionGroup.VEHICLE | PhysicsCollisionGroup.GROUND,
            material: Shared.game.physics.contact.getDefaultGroundMaterial(),
        });

        this.physics.viewsToRotate = [this.bar];
        this.physics.addParts(body);

        this.bar.scale.set(this.params.sx, this.params.sy);
        this.stand.scale.set(this.params.sx, this.params.sy);
        this.stand.y = 45 * this.params.sy;
        this.toInitialAngle();
        this.culling.updateBounds();
    }

    public levelReset(): void
    {
        this.toInitialAngle();
    }

    public levelRespawn(): void
    {
        this.toInitialAngle();
    }

    private toInitialAngle(): void
    {
        this.physics.clearForces();
        this.physics.mainBody.angle = this.params.props?.angle ?? 0.5;
    }
}

export class SeeSawFactory
{
    public static match(data: Record<string, any>): boolean
    {
        return data.type === 'SpriteEntity' && data.img === 'seesaw';
    }

    public static create(game: Game, data: Record<string, any>): void
    {
        const entity = game.pool.get(SeeSawEntity) as SeeSawEntity;

        entity.build(data as SeeSawParams);

        game.level.addEntity(entity);
    }
}

